import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { Session, Project } from '@claude-code-api/shared';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/spinner';
import { toast } from 'sonner';
import { StatusIndicator } from '@/components/ui/status-indicator';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { EmptyState } from '@/components/shared/EmptyState';
import { PageHeader } from '@/components/layout/PageHeader';
import { useInterval } from '@/hooks/useInterval';
import {
  getGiftAgentWorkspaceUrl,
  getProjectWorkflowUrl,
  isTeamWorkflowRunner,
} from '@/lib/gift-agent';
import {
  Terminal,
  Square,
  Play,
  Trash2,
  RotateCcw,
  ExternalLink,
} from 'lucide-react';

const RUNNER_LABEL_KEYS: Record<string, string> = {
  'claude-code': 'claude-code',
  'gift-ui': 'gift-ui',
  acpx: 'acpx',
  'acpx-experimental': 'acpx-experimental',
  'acpx-team': 'acpx-team',
};

interface ContainerStatus {
  status: 'running' | 'stopped' | 'not_found' | 'error';
  containerStatus?: string;
}

export default function SessionsPage() {
  const { t, i18n } = useTranslation(['sessions', 'common']);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [_containerStatusMap, setContainerStatusMap] = useState<
    Record<string, ContainerStatus>
  >({});
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<{
    type: 'stop' | 'restart' | 'delete';
    sessionId: string;
  } | null>(null);

  const sessionsRef = useRef<Session[]>([]);
  sessionsRef.current = sessions;

  const fetchData = useCallback(async () => {
    try {
      const [sessionsRes, projectsRes] = await Promise.all([
        fetch('/api/sessions', { credentials: 'include' }),
        fetch('/api/projects', { credentials: 'include' }),
      ]);

      if (sessionsRes.ok) {
        const data = await sessionsRes.json();
        setSessions(data.data);
      }

      if (projectsRes.ok) {
        const data = await projectsRes.json();
        setProjects(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error(t('sessions:errors.load'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const pollContainerStatuses = useCallback(async () => {
    const currentSessions = sessionsRef.current;
    if (currentSessions.length === 0) return;

    let needsRefresh = false;
    const statusPromises = currentSessions.map(async (session) => {
      try {
        const res = await fetch(`/api/sessions/${session.id}/status`, {
          credentials: 'include',
        });
        if (res.ok) {
          const data = await res.json();
          const statusData = data.data as ContainerStatus & {
            sessionStatus?: string;
            synced?: boolean;
          };
          if (
            statusData.sessionStatus &&
            statusData.sessionStatus !== session.status
          ) {
            needsRefresh = true;
          }
          return { sessionId: session.id, status: statusData };
        }
      } catch {
        // Ignore errors
      }
      return null;
    });

    const results = await Promise.all(statusPromises);
    const newStatusMap: Record<string, ContainerStatus> = {};
    results.forEach((result) => {
      if (result) {
        newStatusMap[result.sessionId] = result.status;
      }
    });
    setContainerStatusMap(newStatusMap);

    if (needsRefresh) {
      fetchData();
    }
  }, [fetchData]);

  useInterval(pollContainerStatuses, 5000);

  useEffect(() => {
    pollContainerStatuses();
  }, [pollContainerStatuses]);

  const getProjectName = (projectId: string) => {
    const project = projects.find((p) => p.id === projectId);
    return project?.name || t('sessions:runnerLabels.unknown');
  };

  const getProjectWorkflowHref = (projectId: string) =>
    getProjectWorkflowUrl(projectId);

  const getRunnerLabel = (runnerType?: string | null) => {
    const key = runnerType ? RUNNER_LABEL_KEYS[runnerType] : undefined;
    return key
      ? t(`sessions:runnerLabels.${key}`)
      : runnerType || t('sessions:runnerLabels.unknown');
  };

  const stopSession = async (sessionId: string) => {
    const session = sessionsRef.current.find((item) => item.id === sessionId);
    const isRunning = session?.status === 'running';

    setActionLoading(`stop-${sessionId}`);
    try {
      const response = await fetch(
        `/api/sessions/${sessionId}/${isRunning ? 'stop' : 'restart'}`,
        {
          method: 'POST',
          credentials: 'include',
        },
      );
      if (response.ok) {
        toast.success(
          isRunning
            ? t('sessions:messages.stopped')
            : t('sessions:messages.started'),
        );
        await fetchData();
      } else {
        toast.error(
          isRunning ? t('sessions:errors.stop') : t('sessions:errors.start'),
        );
      }
    } catch (error) {
      console.error('Failed to stop session:', error);
      toast.error(
        isRunning ? t('sessions:errors.stop') : t('sessions:errors.start'),
      );
    } finally {
      setActionLoading(null);
      setConfirmAction(null);
    }
  };

  const restartSession = async (sessionId: string) => {
    setActionLoading(`restart-${sessionId}`);
    try {
      const response = await fetch(`/api/sessions/${sessionId}/restart`, {
        method: 'POST',
        credentials: 'include',
      });
      if (response.ok) {
        toast.success(t('sessions:messages.restarted'));
        await fetchData();
      } else {
        toast.error(t('sessions:errors.restart'));
      }
    } catch (error) {
      console.error('Failed to restart session:', error);
      toast.error(t('sessions:errors.restart'));
    } finally {
      setActionLoading(null);
      setConfirmAction(null);
    }
  };

  const deleteSession = async (sessionId: string) => {
    setActionLoading(`delete-${sessionId}`);
    try {
      const response = await fetch(`/api/sessions/${sessionId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (response.ok) {
        toast.success(t('sessions:messages.deleted'));
        await fetchData();
      } else {
        const data = await response.json().catch(() => ({}));
        toast.error(data.error || t('sessions:errors.delete'));
      }
    } catch (error) {
      console.error('Failed to delete session:', error);
      toast.error(t('sessions:errors.delete'));
    } finally {
      setActionLoading(null);
      setConfirmAction(null);
    }
  };

  const handleConfirm = () => {
    if (!confirmAction) return;
    const { type, sessionId } = confirmAction;
    if (type === 'stop') stopSession(sessionId);
    else if (type === 'restart') restartSession(sessionId);
    else if (type === 'delete') deleteSession(sessionId);
  };

  const formatElapsed = (date: string | Date) => {
    const now = new Date();
    const created = new Date(date);
    const diffMs = now.getTime() - created.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0)
      return t('sessions:elapsed.dayHour', {
        days: diffDays,
        hours: diffHours % 24,
      });
    if (diffHours > 0)
      return t('sessions:elapsed.hourMinute', {
        hours: diffHours,
        minutes: diffMins % 60,
      });
    return t('sessions:elapsed.minute', { minutes: diffMins });
  };

  const formatDate = (date: string | Date) => {
    return new Intl.DateTimeFormat(i18n.language, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(new Date(date));
  };

  const runningSessions = sessions.filter((s) => s.status === 'running');
  const stoppedSessions = sessions.filter((s) => s.status === 'stopped');
  const targetSession = confirmAction
    ? sessions.find((session) => session.id === confirmAction.sessionId)
    : null;

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader
          title={t('sessions:title')}
          description={t('sessions:description')}
        />
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-16 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  const confirmDialogConfig = confirmAction
    ? {
        stop: {
          title:
            targetSession?.status === 'running'
              ? t('sessions:confirm.stopTitle')
              : t('sessions:confirm.startTitle'),
          description:
            targetSession?.status === 'running'
              ? t('sessions:confirm.stopDescription')
              : t('sessions:confirm.startDescription'),
          confirmLabel:
            targetSession?.status === 'running'
              ? t('sessions:actions.stop')
              : t('sessions:actions.start'),
          variant: 'default' as const,
        },
        restart: {
          title: t('sessions:confirm.restartTitle'),
          description: t('sessions:confirm.restartDescription'),
          confirmLabel: t('sessions:actions.restart'),
          variant: 'default' as const,
        },
        delete: {
          title: t('sessions:confirm.deleteTitle'),
          description: t('sessions:confirm.deleteDescription'),
          confirmLabel: t('sessions:actions.delete'),
          variant: 'destructive' as const,
        },
      }[confirmAction.type]
    : null;

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('sessions:title')}
        description={t('sessions:description')}
      />

      {sessions.length === 0 && (
        <EmptyState
          icon={Terminal}
          title={t('sessions:empty.title')}
          description={t('sessions:empty.description')}
          action={
            <Button asChild>
              <Link to="/projects">{t('sessions:empty.viewProjects')}</Link>
            </Button>
          }
        />
      )}

      {runningSessions.length > 0 && (
        <section>
          <h2 className="text-lg font-semibold font-heading flex items-center gap-2 mb-4">
            {t('sessions:sections.running')}
            <Badge variant="success">{runningSessions.length}</Badge>
          </h2>
          <div className="space-y-3">
            {runningSessions.map((session) => (
              <div
                key={session.id}
                className="bg-card border rounded-lg p-4 flex items-center justify-between"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <StatusIndicator status="running" size="md" />
                  <Link
                    to={
                      isTeamWorkflowRunner(session.runnerType)
                        ? getProjectWorkflowHref(session.projectId)
                        : `/sessions/${session.id}`
                    }
                    className="font-semibold hover:underline truncate"
                  >
                    {getProjectName(session.projectId)}
                  </Link>
                  <Badge variant="secondary">
                    {getRunnerLabel(session.runnerType)}
                  </Badge>
                  <span className="text-sm text-muted-foreground whitespace-nowrap">
                    {formatElapsed(session.createdAt)}
                  </span>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0 ml-4">
                  {(session.runnerType === 'acpx' ||
                    session.runnerType === 'acpx-experimental' ||
                    session.runnerType === 'acpx-team') && (
                    <Button
                      variant="ghost"
                      size="sm"
                      asChild
                      className="gap-1 text-xs mr-1"
                    >
                      {isTeamWorkflowRunner(session.runnerType) ? (
                        <Link to={getProjectWorkflowHref(session.projectId)}>
                          <ExternalLink className="w-3.5 h-3.5" />
                          {t('sessions:actions.openWorkflow')}
                        </Link>
                      ) : (
                        <a
                          href={getGiftAgentWorkspaceUrl(session.id)}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                          {t('sessions:actions.openAgent')}
                        </a>
                      )}
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() =>
                      setConfirmAction({ type: 'stop', sessionId: session.id })
                    }
                    disabled={actionLoading === `stop-${session.id}`}
                    aria-label={t('sessions:actions.stop')}
                  >
                    {actionLoading === `stop-${session.id}` ? (
                      <Spinner className="w-4 h-4" />
                    ) : (
                      <Square className="w-4 h-4" />
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() =>
                      setConfirmAction({
                        type: 'restart',
                        sessionId: session.id,
                      })
                    }
                    disabled={actionLoading === `restart-${session.id}`}
                    aria-label={t('sessions:actions.restart')}
                  >
                    {actionLoading === `restart-${session.id}` ? (
                      <Spinner className="w-4 h-4" />
                    ) : (
                      <RotateCcw className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {stoppedSessions.length > 0 && (
        <section>
          <h2 className="text-lg font-semibold font-heading flex items-center gap-2 mb-4">
            {t('sessions:sections.stopped')}
            <Badge variant="stopped">{stoppedSessions.length}</Badge>
          </h2>
          <div className="space-y-3">
            {stoppedSessions.map((session) => (
              <div
                key={session.id}
                className="bg-card border rounded-lg p-4 flex items-center justify-between"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <StatusIndicator status="stopped" size="md" />
                  <Link
                    to={`/sessions/${session.id}`}
                    className="font-semibold hover:underline truncate"
                  >
                    {getProjectName(session.projectId)}
                  </Link>
                  <Badge variant="secondary">
                    {getRunnerLabel(session.runnerType)}
                  </Badge>
                  <span className="text-sm text-muted-foreground whitespace-nowrap">
                    {formatDate(session.createdAt)}
                  </span>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0 ml-4">
                  {session.containerId && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() =>
                        setConfirmAction({
                          type: 'stop',
                          sessionId: session.id,
                        })
                      }
                      disabled={actionLoading === `stop-${session.id}`}
                      aria-label={t('sessions:actions.start')}
                    >
                      {actionLoading === `stop-${session.id}` ? (
                        <Spinner className="w-4 h-4" />
                      ) : (
                        <Play className="w-4 h-4" />
                      )}
                    </Button>
                  )}
                  {session.containerId && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() =>
                        setConfirmAction({
                          type: 'restart',
                          sessionId: session.id,
                        })
                      }
                      disabled={actionLoading === `restart-${session.id}`}
                      aria-label={t('sessions:actions.restart')}
                    >
                      {actionLoading === `restart-${session.id}` ? (
                        <Spinner className="w-4 h-4" />
                      ) : (
                        <RotateCcw className="w-4 h-4" />
                      )}
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() =>
                      setConfirmAction({
                        type: 'delete',
                        sessionId: session.id,
                      })
                    }
                    disabled={actionLoading === `delete-${session.id}`}
                    aria-label={t('sessions:actions.delete')}
                    className="text-destructive hover:text-destructive"
                  >
                    {actionLoading === `delete-${session.id}` ? (
                      <Spinner className="w-4 h-4" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <ConfirmDialog
        open={!!confirmAction}
        onOpenChange={(open) => {
          if (!open) setConfirmAction(null);
        }}
        title={confirmDialogConfig?.title || ''}
        description={confirmDialogConfig?.description || ''}
        confirmLabel={confirmDialogConfig?.confirmLabel}
        cancelLabel={t('common:cancel')}
        variant={confirmDialogConfig?.variant}
        onConfirm={handleConfirm}
        loading={!!actionLoading}
      />
    </div>
  );
}
